// src/ui/components/AppSidebar/AppSidebar.js
// Sidebar Educa360 (design system). Reemplaza al AdminMenu de Semantic UI.
// Responsive controlado por el layout: recibe `collapsed` (modo iconos).
import React, { useMemo } from "react";
import { ConfigProvider, Menu, Avatar } from "antd";
import { BgColorsOutlined, LogoutOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks";
import { useBrandTheme } from "../../theme/buildAntdTheme";
import {
  useImageSlot,
  useThemeRuntime,
} from "../../theme/ThemeRuntimeProvider";
import { useStylesAccessStore } from "../../../store/stylesAccessStore";
import { getMenuItems, ROLES_WITH_USER_CARD } from "./navConfig";

import logoFull from "../../../assets/png/Educa360Logo.png";
import logoIcon from "../../../assets/png/icoeduca360.png";

import "./AppSidebar.scss";

/**
 * @param {boolean} collapsed - modo iconos (tablet)
 * @param {Function} onNavigate - se invoca tras navegar (cerrar drawer en movil)
 */
export function AppSidebar({ collapsed = false, onNavigate }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { user, logout } = useAuth();
  const brandTheme = useBrandTheme();
  const logoFullSrc = useImageSlot("logo.full", logoFull);
  const logoIconSrc = useImageSlot("logo.icon", logoIcon);

  // Nombre de pagina personalizado por el editor de estilos (override base).
  const { resolvedOverrides } = useThemeRuntime();
  const labelOverride = (labelKey) => {
    const v = resolvedOverrides[`text.${labelKey}`];
    return typeof v === "string" && v.trim() ? v : undefined;
  };

  // El editor de estilos se desbloquea por sesion via OTP (grant). El item solo
  // aparece si hay grant valido.
  const stylesGrant = useStylesAccessStore((s) => s.grant);

  const role = user?.user?.role;
  const items = useMemo(() => {
    const base = getMenuItems(role, t, labelOverride);
    if (stylesGrant) {
      base.push({
        key: "/portal/estilos",
        icon: <BgColorsOutlined />,
        label: "Estilos",
      });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, t, user, resolvedOverrides, stylesGrant]);

  // selecciona el item cuyo path es prefijo del pathname actual (rutas anidadas)
  const selectedKey = useMemo(() => {
    const keys = items.map((i) => i.key);
    return (
      keys.find((k) => pathname === k) ||
      keys.find((k) => pathname.startsWith(k)) ||
      pathname
    );
  }, [items, pathname]);

  const handleClick = ({ key }) => {
    navigate(key);
    onNavigate?.();
  };

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  const showUserCard = ROLES_WITH_USER_CARD.includes(role);
  const fullName =
    `${user?.user?.firstname || ""} ${user?.user?.lastname || ""}`.trim();
  const roleLabel =
    role === "institution" ? t("roles.institution") : t("roles.teacher");

  return (
    <ConfigProvider theme={brandTheme}>
      <div className={`app-sidebar ${collapsed ? "is-collapsed" : ""}`}>
        <div className="app-sidebar__brand">
          <img
            src={collapsed ? logoIconSrc : logoFullSrc}
            alt="Educa360"
            className="app-sidebar__logo"
          />
        </div>

        <Menu
          className="app-sidebar__menu"
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          items={items}
          onClick={handleClick}
        />

        <div className="app-sidebar__footer">
          {showUserCard && (
            <div className="app-sidebar__user">
              <Avatar
                size={collapsed ? 36 : 40}
                src={user?.user?.avatar || undefined}
              >
                {fullName ? fullName[0]?.toUpperCase() : "?"}
              </Avatar>
              {!collapsed && (
                <div className="app-sidebar__user-info">
                  <span className="app-sidebar__user-name">
                    {fullName || "—"}
                  </span>
                  <span className="app-sidebar__user-role">{roleLabel}</span>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="app-sidebar__logout"
            onClick={handleLogout}
            title={t("actions.logout")}
            aria-label={t("actions.logout")}
          >
            <LogoutOutlined />
            {!collapsed && (
              <span className="app-sidebar__logout-label">
                {t("actions.logout")}
              </span>
            )}
          </button>
        </div>
      </div>
    </ConfigProvider>
  );
}
